import {
    extractMainDomain,
    getAdditionalFormTrackingPages,
    shouldEnableFormTracking,
} from '../../../config/SiteConfigManager';
import { logger } from '../../../utils/core/logger';
import type {
    FormDetectionContext,
    FormDetectionManagerInterface,
    FormDetectionStrategy,
    FormElement,
} from '../types';

// Helper function: Check if form passes HTML5 validation before tracking.
//
// Forms with `novalidate` opt out of browser blocking and typically manage
// validation in JS (Unbounce, HubSpot, Rails simple_form, etc.). We still must not
// count a rejected submit as a conversion. Two failure shapes:
//   - valueMissing (empty required field): common on multi-step forms where hidden
//     steps aren't filled yet at submit time → DO track (the flow can still be valid).
//   - malformed value (typeMismatch/patternMismatch/…): the user typed something the
//     server will reject (e.g. a bad email) → DO NOT track (would be a false conversion).
const MALFORMED_VALUE_KEYS = [
    'typeMismatch',
    'patternMismatch',
    'stepMismatch',
    'rangeOverflow',
    'rangeUnderflow',
    'tooLong',
    'tooShort',
    'badInput',
] as const;

function hasMalformedFieldValue(form: FormElement): boolean {
    const fields = form.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >('input, select, textarea');
    for (const field of Array.from(fields)) {
        const validity = field.validity;
        if (validity.valid) {
            continue;
        }
        if (MALFORMED_VALUE_KEYS.some((key) => validity[key])) {
            logger.debug('⚠️ Malformed field value, skipping tracking', {
                formId: form.id,
                field: field.name || field.tagName,
            });
            return true;
        }
    }
    return false;
}

function shouldTrackFormSubmission(form: FormElement): boolean {
    if (form.noValidate) {
        // JS-managed validation: block only malformed values, allow empty (multi-step).
        return !hasMalformedFieldValue(form);
    }
    if (!form.checkValidity()) {
        logger.debug('⚠️ Form validation failed, skipping tracking', {
            formId: form.id,
            invalidFields: form.querySelectorAll(':invalid').length,
        });
        return false;
    }
    return true;
}

// Keywords in button text that indicate a submit action (for type="button" detection)
const SUBMIT_KEYWORDS = [
    // English
    'submit',
    'send',
    'sign up',
    'register',
    'book',
    'schedule',
    'request',
    'get started',
    'create account',
    'contact',
    // French
    'envoyer',
    'soumettre',
    'réserver',
    'demander',
    'contacter',
    // Spanish
    'enviar',
    'registrar',
    'reservar',
    'solicitar',
    'contactar',
    // German
    'absenden',
    'registrieren',
    'buchen',
    'anfordern',
    'kontaktieren',
];

// Strategy 3: CTA Page Form Detection (Standard form tracking with result detection)
export class CTAPageFormStrategy implements FormDetectionStrategy {
    name = 'cta_page_form';
    private manager: FormDetectionManagerInterface | null = null;

    setManager(manager: FormDetectionManagerInterface): void {
        this.manager = manager;
    }

    canHandle(form: FormElement, context: FormDetectionContext): boolean {
        // Check if form tracking is enabled for this page
        // This includes CTA destination pages AND additional configured patterns

        const mainDomain = extractMainDomain(context.domain);
        const additionalPatterns = getAdditionalFormTrackingPages(mainDomain);

        const shouldTrack = shouldEnableFormTracking(context.domain);

        logger.debug('🎯 CTAPageFormStrategy.canHandle check', {
            formId: form.id,
            domain: context.domain,
            mainDomain: mainDomain,
            currentUrl: context.currentUrl,
            hasAdditionalPatterns: additionalPatterns.length > 0,
            patterns: additionalPatterns,
            shouldTrack,
        });

        return shouldTrack;
    }

    apply(form: FormElement, context: FormDetectionContext): void {
        // Track both submit events AND button clicks (for forms using Stimulus/Turbo/AJAX)
        const trackSubmission = () => {
            // Check deduplication first
            if (this.manager && !this.manager.shouldTrackSubmission(form)) {
                return;
            }

            const fieldCount = form.querySelectorAll('input, select, textarea').length;

            // Check if form will cause navigation
            let willNavigate = false;
            if (form.action) {
                try {
                    const formActionUrl = new URL(form.action, window.location.href);
                    const currentUrl = new URL(window.location.href);

                    // Check if action points to different page or different domain
                    if (
                        formActionUrl.origin !== currentUrl.origin ||
                        formActionUrl.pathname !== currentUrl.pathname
                    ) {
                        willNavigate = true;
                    }
                } catch {
                    // URL parsing error - likely relative URL, ignore
                }
            }

            logger.debug('🎯 CTA form submission via submit event', {
                formId: form.id,
                fieldCount,
                willNavigate,
                formAction: form.action,
            });

            const baseData = {
                formId: form.id || 'cta_form',
                formClass: form.className || '',
                pageUrl: context.currentUrl,
                formAction: form.action || '',
                formMethod: form.method || 'POST',
                fieldCount,
                detectionMethod: this.name,
            };

            if (willNavigate) {
                // Form will navigate away - track immediately as success
                // We won't be here to detect the result after navigation
                logger.debug('🧭 Form will navigate away, tracking as success');
                context.onFormSubmit({
                    ...baseData,
                    status: 'success',
                    indicators: ['form_will_navigate'],
                });
            } else {
                // Form stays on page - track with unknown, then detect result
                context.onFormSubmit({
                    ...baseData,
                    status: 'unknown' as const,
                });

                // After a delay, detect the result using heuristics
                setTimeout(() => {
                    if (this.manager) {
                        const result = this.manager.detectFormResult(form);

                        logger.debug(`📊 CTA form submission result detected`, {
                            formId: form.id,
                            status: result.status,
                            indicators: result.indicators,
                        });

                        // Track again with detected status if it changed from unknown
                        if (result.status !== 'unknown') {
                            context.onFormSubmit({
                                ...baseData,
                                status: result.status,
                                indicators: result.indicators,
                            });
                        }
                    }
                }, 2000); // Wait 2 seconds for DOM changes
            }
        };

        // Listen for submit events (standard form submission).
        //
        // Some third-party scripts (Unbounce, HubSpot, etc.) attach their own
        // bubble-phase submit listeners and call stopImmediatePropagation, which
        // would prevent our form-level bubble listener from firing. Attach at
        // the document capture phase as well so we fire before any bubble-phase
        // blocker runs. Filter to this form so the listener only triggers for
        // its own submissions, and rely on the manager's deduplication to merge
        // the capture-phase and bubble-phase invocations.
        const submitHandler = (event: Event) => {
            if (event.target !== form) {
                return;
            }
            if (!shouldTrackFormSubmission(form)) {
                return;
            }
            trackSubmission();
        };
        form.addEventListener('submit', submitHandler);
        document.addEventListener('submit', submitHandler, true);

        // Also listen for button clicks (for forms using Stimulus/Turbo/AJAX that prevent default submit)
        // Include type="button" buttons with submit-like text (common in React/SPA forms)
        const submitButtons = form.querySelectorAll(
            'button[type="submit"], input[type="submit"], button[type="button"]'
        );

        // Check if form will navigate away (needed to decide timing)
        let willNavigate = false;
        if (form.action) {
            try {
                const formActionUrl = new URL(form.action, window.location.href);
                const currentUrl = new URL(window.location.href);
                if (
                    formActionUrl.origin !== currentUrl.origin ||
                    formActionUrl.pathname !== currentUrl.pathname
                ) {
                    willNavigate = true;
                }
            } catch {
                // URL parsing error - ignore
            }
        }

        submitButtons.forEach((button) => {
            // For type="button" buttons, only track if text matches submit keywords
            // This avoids false positives from toggle/close/utility buttons
            if (button instanceof HTMLButtonElement && button.type === 'button') {
                const text = button.textContent?.trim().toLowerCase() || '';
                const isSubmitLike = SUBMIT_KEYWORDS.some((kw) => text.includes(kw));
                if (!isSubmitLike) return;
            }

            button.addEventListener('click', () => {
                logger.debug('🖱️ CTA form submit button clicked', {
                    formId: form.id,
                    buttonText: button.textContent?.trim() || '',
                    hasStimulus: button.hasAttribute('data-action'),
                    willNavigate,
                });

                if (willNavigate) {
                    // Form will navigate - must check validation and track IMMEDIATELY before page unload
                    // Use requestAnimationFrame to check after current event loop (validation has run)
                    requestAnimationFrame(() => {
                        const isValid = form.checkValidity();

                        logger.debug('📋 Form validation check (pre-navigation)', {
                            formId: form.id,
                            isValid,
                            invalidFields: isValid ? 0 : form.querySelectorAll(':invalid').length,
                        });

                        if (isValid) {
                            // Track immediately - page will navigate soon
                            trackSubmission();
                        }
                    });
                } else {
                    // Form stays on page - can afford to wait longer for validation
                    setTimeout(() => {
                        const isValid = form.checkValidity();

                        logger.debug('📋 Form validation check after button click', {
                            formId: form.id,
                            isValid,
                            invalidFields: isValid ? 0 : form.querySelectorAll(':invalid').length,
                        });

                        if (isValid) {
                            trackSubmission();
                        } else {
                            logger.debug('⚠️ Form submission not tracked - validation failed', {
                                formId: form.id,
                            });
                        }
                    }, 100);
                }
            });
        });
    }
}
